/**
 * Map: Main react component. Other components initialized here with shared state varaibles. 
 */

// React related imports
import React, { useEffect, memo, useRef, useState } from 'react';
import { Provider, useDispatch, useSelector } from 'react-redux';

// Leaflet related imports
import { CRS } from 'leaflet';
import L from 'leaflet';
import { GeoJSON, MapContainer, TileLayer, ZoomControl } from 'react-leaflet';
import { useMap, useMapEvents } from 'react-leaflet/hooks';
import './SmoothWheelZoom.js';

// React-device-detect imports
import { isChrome, isMobile, isSafari, isTablet } from 'react-device-detect';

// Alchemy related imports
import { Alchemy, Network } from 'alchemy-sdk';

// Redux related imports
import store from './redux/store';

// Config imports
import config from '../data/config.json';
import contract from './redux/config/config.json';

// JSON data imports
import polygonJson from '../data/geo_json_string.json';
import ownership_full from '../data/availability_list.json';

// Component imports
import Sidebar, { defaultSidebarHeight } from './Sidebar';
import ListView from './ListView.js';
import Topbar from './Topbar';
import Popout from './Popout';
import Information from './Information';
import BottomBar from './BottomBar';
import Splash from './Splash';
import DetectDevice from './DeviceDetector.js';
import MintMessages from './MintMessages';
import styled from 'styled-components';
import configuration from '../data/config.json';
import Web3 from 'web3';

//  WEB3 Set UP
var alch_network;
var alch_key;
var contractAddress;

//Code to automatically switch blockchain from testnet to mainnet
if (contract.NETWORK.ID == 1) {
  alch_key = contract.ALCH_KEY;
  alch_network = Network.ETH_MAINNET;
  contractAddress = String(contract.CONTRACT_ADDRESS);
}
if (contract.NETWORK.ID == 5) {
  alch_key = contract.ALCH_KEY_TEST;
  alch_network = "eth-sepolia"; //Network.ETH_SEPOLIA;
  contractAddress = String(contract.CONTRACT_ADDRESS_GOERLI);
}

const alc_config = {
  apiKey: alch_key,
  network: alch_network,
};
const alchemy = new Alchemy(alc_config);

// Global vars
var selectedLayer = null;
var overlay_opacity = null; // Outline
var fill_overlay_opacity = null;
var mouseover_opacity = null;
var sidebar = null;
var isMouseDown = false;
var lagLayerTimeout = null;
var isProgrammticZoom = false;

// Helper variables to prevents double rendering
var hasRendered = false;
var newZoom;

// Outside border
const FrameWithRoundedCorners = styled.div`
  position: relative;

  &::before {
    position: absolute;
    box-shadow: 7px 7px ${configuration.frame_background_color},
      -7px -7px ${configuration.frame_background_color},
      7px -7px ${configuration.frame_background_color},
      -7px 7px ${configuration.frame_background_color};
  }
`;

/**
 * Landing page: when user arrives, snap to random, then pan, and display splash screen
 */
export var splashStillUp = 1;
export const StartAtRandomTile = ({ mainTL, sync_flag, skipSplash }) => {
  var currentUrl = window.location.href;
  var tileUrl = currentUrl.split('/');
  const map = useMap();
  const wasReload =
    (window.performance.navigation &&
      window.performance.navigation.type === 1) ||
    window.performance
      .getEntriesByType('navigation')
      .map((nav) => nav.type)
      .includes('reload');

  function snapToRandom() {
    let randomNum = Math.floor(Math.random() * 100);
    let randomCoords =
      polygonJson.features[0].features[randomNum].features[0].geometry
        .coordinates;
    let mapSize = map.getSize();
    let mapy = window.visualViewport.height - (35 + 40); // 35 and 40 comes from bottom and topbar
    let mapx = mapSize.x;
    let screenratio = mapx / mapy;

    let topleft = randomCoords[0][0];
    let bottomRight = randomCoords[0][2];
    let lat_length = topleft[1] - bottomRight[1];
    let lat_width = bottomRight[0] - topleft[0];
    let centerpoint = [
      (topleft[1] + bottomRight[1]) / 2,
      (bottomRight[0] + topleft[0]) / 2,
    ];
    let layerRatio = lat_width / lat_length;
    const newZoom = 3.7 + Math.random() * (5.3 - 3.7);

    centerpoint = map.unproject(map.project(centerpoint, newZoom), newZoom);
    map.setView(centerpoint, newZoom);
  }

  const handleMoveEnd = () => {
    setTimeout(() => {
      let pan_ongoing = document
        .getElementsByClassName('leaflet-map-pane')[0]
        .classList.contains('leaflet-pan-anim');
      let projTitle = document.getElementsByClassName('project-title')[0];
      if (
        null != projTitle &&
        projTitle.style.visibility == 'visible' &&
        splashStillUp
      ) {
        map.off('click', handleMoveEnd);
        map.off('dragstart', handleMoveEnd);
        map.off('moveend', handleMoveEnd);
        map.stop();

        map.smoothWheelZoom.enable();
        map.touchZoom.enable();
        map.dragging.enable();
        splashStillUp = 0;

        let playbox = document.getElementsByClassName(
          'bttn frame-controller'
        )[0];
        if (playbox) {
          playbox.style.visibility = 'visible';
        }

        if (sync_flag == 0) {
          if (mainTL.current) {
            mainTL.current.setOpacity(1);
          }
        }
      } else if (
        !pan_ongoing &&
        (null == projTitle || projTitle.style.visibility != 'visible')
      ) {
        slowZoomToRandom();
      }
    }, 100);
  };

  function getDistanceToOtherLatLng(otherLatLng) {
    // Get the current center of the map viewport in latlng
    let centerLatLng = map.getCenter();
    let pointC = map.latLngToContainerPoint(centerLatLng); // convert to containerpoint (pixels)
    let pointO = map.latLngToContainerPoint(otherLatLng); // convert to containerpoint (pixels)
    return Math.abs(
      Math.sqrt(
        Math.pow(pointC.x - pointO.x, 2) + Math.pow(pointC.y - pointO.y, 2)
      )
    );
  }

  function slowZoomToRandom() {
    const min_lat = 67.96875;
    const max_lat = 385.15625;
    const random_lat = -(min_lat + Math.random() * (max_lat - min_lat));

    const min_lng = 117.1875;
    const max_lng = 664.0625;
    const random_lng = min_lng + Math.random() * (max_lng - min_lng);

    // Think about this as pixels/second
    let panspeed = 25 + Math.random() * (50 - 25);
    // let panspeed = 1700

    map.panTo([random_lat, random_lng], {
      animate: true,
      duration: getDistanceToOtherLatLng([random_lat, random_lng]) / panspeed,
      easeLinearity: 1,
    });
  }

  useEffect(() => {
    if (!skipSplash) {
      // Allow only one instance
      if (!hasRendered) {
        map.on('moveend', handleMoveEnd);
        map.on('click', handleMoveEnd);

        hasRendered = true;
        map.touchZoom.disable();

        // Go to a random x,y,z
        snapToRandom();

        // Start panning
        setTimeout(() => {
          slowZoomToRandom();
        }, 300);
      }
    } else {
      var projTitle = document.getElementsByClassName('project-title')[0];
      projTitle.style.visibility = 'visible';
    }
  });
  return null;
};

/**
 * Map navigation and frame behavior 
 */
export const MapEvents = ({
  mainTL,
  setAllOverlays,
  allOverlays,
  subregionMode,
  sync_flag,
  setFrameNum,
  animationStartTime,
  isPaused,
  projectInfo,
  setCurrZoom,
  currZoom,
  setUpdateOnZoom,
  tilePreview,
  renderNumber,
}) => {
  const map = useMap();
  const [oldTilePreview, setOldTilePreview] = useState(-2);

  const handleMouseUp = () => {
    isMouseDown = false;
    const mouseUpHelper = () => {
      if (sync_flag.current === 0) {
        if (mainTL.current && !isPaused) {
          mainTL.current.setOpacity(1);
        }
      }
    };
    setTimeout(mouseUpHelper, 125); // Show webp layer on mouse up
  };

  useEffect(() => {
    const mapContainer = map.getContainer();

    const handleTouchStart = (e) => {
      isMouseDown = true;
    };

    const handleTouchMove = (e) => {
      if (isMouseDown && e.touches.length === 0) {
        handleMouseUp();
      }
    };

    const handleTouchend = (e) => {
      isMouseDown = false;
      handleMouseUp();
    };

    L.DomEvent.on(mapContainer, 'touchstart', handleTouchStart);
    L.DomEvent.on(mapContainer, 'touchmove', handleTouchMove);
    L.DomEvent.on(mapContainer, 'touchend', handleTouchend);

    return () => {
      L.DomEvent.off(mapContainer, 'touchstart', handleTouchStart);
      L.DomEvent.off(mapContainer, 'touchmove', handleTouchMove);
      L.DomEvent.off(mapContainer, 'touchend', handleTouchend);
    };
  }, [map]);

  useMapEvents({
    movestart: () => {
      if (mainTL.current && mainTL.current.options.opacity == 1) {
        var num_keyframes_current_render;
        var duration;
        if (renderNumber == 42 || renderNumber == 56) {
          num_keyframes_current_render = configuration.num_key_frames;
          duration = 10000;
        }
        if (renderNumber == 14) {
          num_keyframes_current_render = configuration.num_key_frames_render14;
          duration = 6400;
        }
        let msElapsed = (Date.now() - animationStartTime) % duration;
        setFrameNum(
          Math.round((num_keyframes_current_render * msElapsed) / duration) %
            num_keyframes_current_render
        );
      }
      if (mainTL.current) {
        mainTL.current.setOpacity(0); // Hide webp layer when moving; splash still up means don't hide at beginning
      }

      let sidebar = document.getElementsByClassName('sidebar')[0];
      if (DetectDevice && undefined != sidebar) {
        let playbox = document.getElementsByClassName(
          'bttn frame-controller'
        )[0];
        playbox.style.visibility = 'hidden';
      }
    },

    moveend: () => {
      let sidebar = document.getElementsByClassName('sidebar')[0];

      if (DetectDevice && isProgrammticZoom) {
        let playbox = document.getElementsByClassName(
          'bttn frame-controller'
        )[0];
        if (!projectInfo) {
          playbox.style.visibility = 'visible';
        }
      }
      if (DetectDevice && sidebar && sidebar.clientHeight > 350) {
        let playbox = document.getElementsByClassName(
          'bttn frame-controller'
        )[0];
        playbox.style.visibility = 'hidden';

        let loading = document.getElementsByClassName('loading')[0];
        if (loading) {
          loading.style.display = 'none';
          loading.style.visibility = 'hidden';
        }
      }

      if (isProgrammticZoom) {
        isProgrammticZoom = false;
      }
    },

    mousedown: () => {
      isMouseDown = true;
    },

    mousemove: (e) => {
      if (isMouseDown && e.originalEvent.buttons === 0) {
        handleMouseUp();
      }
    },

    mouseup: () => {
      handleMouseUp();
    },

    zoomstart: () => {
      setAllOverlays(0);
      if (mainTL.current && mainTL.current.options.opacity == 1) {
        var num_keyframes_current_render;
        var duration;
        if (renderNumber == 42 || renderNumber == 56) {
          duration = 10000;
          num_keyframes_current_render = configuration.num_key_frames;
        }
        if (renderNumber == 14) {
          duration = 6400;
          num_keyframes_current_render = configuration.num_key_frames_render14;
        }
        let msElapsed = (Date.now() - animationStartTime) % duration;
        setFrameNum(
          Math.round((num_keyframes_current_render * msElapsed) / duration) %
            num_keyframes_current_render
        );
      }
      if (mainTL.current) {
        mainTL.current.setOpacity(0); // Hide webp layer when zooming
      }
    },

    zoom: () => {
      setAllOverlays(0);
      if (mainTL.current) {
        mainTL.current.setOpacity(0); // Hide webp layer when zooming
      }
    },

    zoomend: () => {
      if (subregionMode == -1) {
        setCurrZoom(newZoom);
      }
      if (oldTilePreview == -1 && tilePreview !== -1) {
        setUpdateOnZoom(true);
        setOldTilePreview(tilePreview);
      } else {
        setOldTilePreview(tilePreview);
      }
      setAllOverlays(1);
      if (selectedLayer != null) {
        selectedLayer.setStyle({
          fillOpacity: 0,
          color: config.tile_select_color,
          weight: 3,
        });
      }

      setTimeout(handleMouseUp, 100);
      if (!splashStillUp && (!DetectDevice || subregionMode == -1)) {
        map.touchZoom.enable();
        map.dragging.enable();
      }
    },
  });
  return null;
};

// Takes geometry of tile, returns width by height
export const getDimensions = (geometry) => {
  return [
    Math.round(
      128 * (geometry.coordinates[0][3][0] - geometry.coordinates[0][0][0])
    ),
    Math.round(
      128 * (geometry.coordinates[0][0][1] - geometry.coordinates[0][1][1])
    ),
  ];
};

var my_time;
var count = 0;
function promptScroll() {
  var count = 0;

  // If condition to set repeat
  if (count < 8) {
    var objDiv = document.getElementById('scrolling-prompt');
    if (!objDiv) {
      return;
    }
    objDiv.scrollTop = objDiv.scrollTop + 1;
    if (objDiv.scrollTop == objDiv.scrollHeight - 10) {
      setTimeout(function () {
        objDiv.scrollTop = 0;
        count++;
      }, 100);
    }
    //set scrolling time start
    my_time = setTimeout(promptScroll, 100);
    //set scrolling time end
  }
}

// classA-0001 --> 0001
function getNum(numString) {
  let num = numString.split('-')[1];
  return num;
}

/**
 * Select a random tile: most important elements of the site are set to update on masterTile update. MasterTile is selected Tile. 
 */
export const selectRandom = ({
  map,
  gridOverlay,
  sidebarState,
  setMasterTile,
  setSidebarTitle,
  shouldZoom,
  setCurrentPrompt,
  isMobileFullView,
}) => {
  let numTiles = polygonJson.features[0].features.length;
  let randomNum = Math.floor(Math.random() * (numTiles - 2) + 1);
  let randomFeature = polygonJson.features[0].features[randomNum].features[0];

  var newMasterTile = {
    masterTile: {
      name: randomFeature.properties.name,
      tokenId: randomFeature.properties.tokenId,
      sentimentX_token: randomFeature.properties.sentimentX_token,
      sentimentY_token: randomFeature.properties.sentimentY_token,
      img_prompt: randomFeature.properties.img_prompt,
      wiki_token: randomFeature.properties.wiki_token,
      rgb: randomFeature.properties.rgb,
      dimensions: getDimensions(randomFeature.geometry),
    },
  };
  setSelectedSubregionNull();
  setMasterTile(newMasterTile);
  setSidebarTitle(randomFeature.properties.tokenId);

  if (gridOverlay.current) {
    var urlId = '/todem/' + getNum(randomFeature.properties.name);
    window.history.pushState('', '', '' + urlId);
    selectedLayer = gridOverlay.current.getLayers()[randomNum];
    gridOverlay.current.getLayers()[randomNum].setStyle({
      opacity: 1,
      color: config.tile_select_color,
    });
    gridOverlay.current.getLayers()[randomNum].bringToFront();
  }

  if (shouldZoom) {
    zoomToLayer(map, randomFeature, sidebarState, isMobileFullView);
  }
};

/**
 * Select next tile: set as selected then zoom to tile. 
 */
export const selectNext = ({
  map,
  gridOverlay,
  sidebarState,
  setMasterTile,
  setSidebarTitle,
  shouldZoom,
  currentTile,
  setCurrentPrompt,
  isMobileFullView,
}) => {
  let currentTileName = currentTile.name;
  let nextNum = parseInt(currentTileName.split('-')[1], 10) + 1;
  if (nextNum > 999) {
    nextNum = 0;
  }
  let nextFeature = polygonJson.features[0].features[nextNum].features[0];

  var newMasterTile = {
    masterTile: {
      name: nextFeature.properties.name,
      tokenId: nextFeature.properties.tokenId,
      sentimentX_token: nextFeature.properties.sentimentX_token,
      sentimentY_token: nextFeature.properties.sentimentY_token,
      img_prompt: nextFeature.properties.img_prompt,
      wiki_token: nextFeature.properties.wiki_token,
      rgb: nextFeature.properties.rgb,
      dimensions: getDimensions(nextFeature.geometry),
    },
  };
  setSelectedSubregionNull();
  setMasterTile(newMasterTile);
  setSidebarTitle(nextFeature.properties.tokenId);

  if (gridOverlay.current) {
    selectedLayer = gridOverlay.current.getLayers()[nextNum];
    gridOverlay.current.getLayers()[nextNum].bringToFront();
    var urlId = '/todem/' + getNum(nextFeature.properties.name);
    window.history.pushState('', '', '' + urlId);
  }

  if (shouldZoom) {
    zoomToLayer(map, nextFeature, sidebarState, isMobileFullView);
  }
};

/**
 * Select prev tile: set as selected then zoom to tile. 
 */
export const selectPrevious = ({
  map,
  gridOverlay,
  sidebarState,
  setMasterTile,
  setSidebarTitle,
  shouldZoom,
  currentTile,
  setCurrentPrompt,
  isMobileFullView,
}) => {
  let currentTileName = currentTile.name;
  let previousNum = parseInt(currentTileName.split('-')[1], 10) - 1;
  if (previousNum < 0) {
    previousNum = 999;
  }
  let previousFeature =
    polygonJson.features[0].features[previousNum].features[0];

  var newMasterTile = {
    masterTile: {
      name: previousFeature.properties.name,
      tokenId: previousFeature.properties.tokenId,
      sentimentX_token: previousFeature.properties.sentimentX_token,
      sentimentY_token: previousFeature.properties.sentimentY_token,
      img_prompt: previousFeature.properties.img_prompt,
      wiki_token: previousFeature.properties.wiki_token,
      rgb: previousFeature.properties.rgb,
      dimensions: getDimensions(previousFeature.geometry),
    },
  };
  setSelectedSubregionNull();
  setMasterTile(newMasterTile);
  setSidebarTitle(previousFeature.properties.tokenId);

  if (gridOverlay.current) {
    selectedLayer = gridOverlay.current.getLayers()[previousNum];
    gridOverlay.current.getLayers()[previousNum].bringToFront();
    var urlId = '/todem/' + getNum(previousFeature.properties.name);
    window.history.pushState('', '', urlId);
  }

  if (shouldZoom) {
    zoomToLayer(map, previousFeature, sidebarState, isMobileFullView);
  }
};

/**
 * Select specific tile: set tile as selected then zoom to tile.
 */
export const selectQueried = ({
  map,
  gridOverlay,
  sidebarState,
  setMasterTile,
  setSidebarTitle,
  setTilePreview,
  shouldZoom,
  queryNumObject,
  setCurrentPrompt,
}) => {
  var queryNum = parseInt(queryNumObject.number);
  if (!(queryNum != NaN && 0 <= queryNum && queryNum < 1000)) {
    return;
  }

  let queriedFeature = polygonJson.features[0].features[queryNum].features[0];

  var newMasterTile = {
    masterTile: {
      name: queriedFeature.properties.name,
      tokenId: queriedFeature.properties.tokenId,
      sentimentX_token: queriedFeature.properties.sentimentX_token,
      sentimentY_token: queriedFeature.properties.sentimentY_token,
      img_prompt: queriedFeature.properties.img_prompt,
      wiki_token: queriedFeature.properties.wiki_token,
      rgb: queriedFeature.properties.rgb,
      dimensions: getDimensions(queriedFeature.geometry),
    },
  };
  let parentTileTemp = parseInt(getNum(queriedFeature.properties.name));
  setTilePreview(parentTileTemp);
  //console.log(setTilePreview, setSidebarTitle)
  setSelectedSubregionNull();
  setMasterTile(newMasterTile);
  setSidebarTitle(queriedFeature.properties.tokenId);

  if (gridOverlay.current) {
    selectedLayer = gridOverlay.current.getLayers()[queryNum];
    gridOverlay.current.getLayers()[queryNum].bringToFront();
    var urlId = getNum(queriedFeature.properties.name);
    window.history.pushState('', '', '/todem/' + urlId);
  }

  if (shouldZoom) {
    zoomToLayer(map, queriedFeature, sidebarState);
  }
  /*
  if (gridOverlay.current) {
    selectedLayer = gridOverlay.current.getLayers()[queryNum];
    gridOverlay.current.getLayers()[queryNum].bringToFront();
  }

  if (shouldZoom) {
    zoomToLayer(map, queriedFeature, sidebarState);
    var urlId = getNum(queriedFeature.properties.name);
    window.history.pushState('', '', '/todem/' + urlId);
  }*/
};


export const selectSubregionLayer = (
  { promptOverlayRef },
  subregionNumObject
) => {
  // This may need some revisiting
  var subregionNum = parseInt(subregionNumObject.number);
  if (promptOverlayRef.current) {
    selectedSubregion = [
      promptOverlayRef.current.getLayers()[subregionNum],
      subregionNumObject.prompt,
    ];
  } else {
    // console.log("promptOverlay not set")
  }
};

// Uses flyTo() to align a given GEOJSON feature directly in center of viewport
// Makes assumptions about the dimensions of sidebar and topbar
export const zoomToLayer = (map, feature, sidebarState, isMobileFullView) => {
  setTimeout(() => {
    let mapSize = map.getSize();
    let bottomsb = window.innerWidth < 600;
    let mapy;
    if (isMobile || isTablet) {
      mapy = window.innerHeight - (!bottomsb * 35 + 40); // 35 and 40 comes from bottom and topbar
      // mapy = window.innerHeight - 40 // 35 and 40 comes from bottom and topbar
    } else {
      mapy = window.visualViewport.height - (!bottomsb * 35 + 40); // 35 and 40 comes from bottom and topbar
    }
    let mapx = mapSize.x;

    var playbox = document.getElementsByClassName('bttn frame-controller')[0];

    // Figurings out point to zoom into
    let heightSB = document.getElementsByClassName('sidebar')[0];
    if (heightSB) {
      if (isMobileFullView == true) {
        heightSB = defaultSidebarHeight;
      } else {
        heightSB = heightSB.offsetHeight;
      }
      //console.log(heightSB)
    } else {
      heightSB = 250;
    }

    let listView = document.getElementsByClassName('list-view')[0];
    listView = listView && !listView.classList.contains('hidden');
    if (listView) {
      mapx -= 306;
    }

    // Acount for sidebar
    if (bottomsb) {
      mapy -= heightSB;
    } else {
      mapx -= 306;
    }
    let screenratio = mapx / mapy;

    // Calculates center, dimensions of feature to zoom to
    let zoomCoords = feature.geometry.coordinates;
    let topleft = zoomCoords[0][0];
    let bottomRight = zoomCoords[0][2];
    let lat_length = topleft[1] - bottomRight[1];

    if (DetectDevice) {
      lat_length += 0.390625; // hack to add 50px to
    }

    let lat_width = bottomRight[0] - topleft[0];
    let centerpoint = [
      (topleft[1] + bottomRight[1]) / 2,
      (bottomRight[0] + topleft[0]) / 2,
    ];
    let layerRatio = lat_width / lat_length;

    //var newZoom
    // Calculates new zoom level depending on how tile dimensions compare to screen dimensions
    // Answers: Will the layer's width or height be the contraining factor?
    // Changing the multiplier will change the amount of padding

    if (layerRatio < screenratio) {
      newZoom = Math.log2(mapy / (1.2 * lat_length));
    } else {
      newZoom = Math.log2(mapx / (1.15 * lat_width));
    }

    let offset = [
      !bottomsb * (306 / 2) + !!listView * (-306 / 2),
      15 * isMobile + (bottomsb * heightSB) / 2 + -40 / 2 + 10 * !isMobile,
    ];
    // let offset = [(!bottomsb) * (280 / 2), (bottomsb * heightSB / 2) + (-5 / 2)]
    //console.log(centerpoint);
    //console.log(offset);
    //console.log(newZoom);
    centerpoint = map.unproject(
      map.project(centerpoint, newZoom).add(offset),
      newZoom
    );
    //console.log(centerpoint);

    if (DetectDevice) {
      var playbox = document.getElementsByClassName('bttn frame-controller')[0];
      if (playbox) {
        if (isMobileFullView == true) {
          // console.log(isMobileFullView)
          playbox.style.visibility = 'hidden';
          // console.log(playbox.style.visibility)
        } else {
          // console.log(isMobileFullView)
          let centerguess = 40 + (mapy - 30) / 2;

          lat_length -= 0.390625;
          // console.log(centerpoint)
          let tile_height_pixels = map.project(
            { lat: lat_length / 2, lng: 0 },
            newZoom
          ).y;

          playbox.style.transform = `translate(calc(50vw - 50px), calc(${
            centerguess + Math.abs(tile_height_pixels) + 10
          }px))`;
        }
      }
    }

    if (!(map.getCenter() == centerpoint && map.getZoom() == newZoom)) {
      isProgrammticZoom = true;
      // Disables map interaction during flyTo
      map.dragging.disable();
      map.touchZoom.disable();

      map.flyTo(centerpoint, newZoom, { animate: 1, easeLinearity: 0 });
    }

    // Wipes old selected layer styling
    if (selectedLayer != null) {
      selectedLayer.setStyle({
        color: config.tile_default_color,
      });
    }
  }, 10);
};

/**
 * Subreigions allow us to differentiate between the first and second level of selection. First Tile is selected. Then subregion. 
 */
export var selectedSubregion = [null, null];

// clear subregion selection
export const setSelectedSubregionNull = () => {
  selectedSubregion = [null, null];
};

export var subregionSelected = false;
var parentNumber = null;
var startScrolling;
var singleSubregion;

/**
 * Component to display the selected prompt. This is where the logic for determining if a subregion is selected lives (dark blue vs light blue highlight). 
 * This logic changes in art mode vs. web3 mode. 
 */
export const PromptOverlay = ({
  promptOverlayRef,
  currentPrompt,
  singleRegion,
  masterTile,
  setCurrentPrompt,
  setSingleRegion,
  allOverlays,
  subregionMode,
  setSubregionMode,
  setTilePreview,
  tilePreview,
  currZoom,
  setCurrZoom,
  updateOnZoom,
  setUpdateOnZoom,
  redactNFTs,
}) => {
  const [promptData, setPrompt] = useState({
    type: 'FeatureCollection',
    features: [{ type: 'FeatureCollection', features: [] }],
  });
  const [oldTilePreview, setOldTilePreview] = useState(-2);
  const map = useMap();

  function getNum(numString) {
    let num = numString.split('-')[1];
    return num;
  }
  var rand = Math.random();

  const fetchData = async () => {
    const fetcher = async () => {
      const response = await fetch(
        'https://www.latentculture.com/tohm/galactic-react/src/data/small_geojsons/' +
          masterTile.masterTile.name +
          '.json'
      );
      return response.json();
    };
    var prompt_data = await fetcher();
    //console.log(prompt_data.features)
    //setPrompt(inset(prompt_data));
    setPrompt(prompt_data);

    rand = Math.random();
    parentNumber = parseInt(getNum(masterTile.masterTile.name));
    if (
      !redactNFTs &&
      tilePreview !== -1 &&
      !DetectDevice &&
      prompt_data.features[0].features.length == 1
    ) {
      setSubregionMode(-1);
      //setSubregionMode(parentNumber)
      singleSubregion = true;
      subregionSelected = true;
      let feature = prompt_data.features[0].features[0].features[0];
      setCurrentPrompt([
        feature.id,
        feature.properties.img_prompt,
        feature.geometry,
        feature.properties.wiki,
      ]);
      setSingleRegion(true);

      var urlId =
        '/todem/' +
        parentNumber.toString().padStart(4, '0') +
        '.' +
        feature.id.toString().padStart(4, '0');
      window.history.pushState('', '', '' + urlId);
    } else {
      singleSubregion = false;
      if (parentNumber != subregionMode) {
        setSubregionMode(-1);
        map.touchZoom.enable();
        map.dragging.enable();
        subregionSelected = false;
      }
    }
  };

  useEffect(() => {
    if (masterTile.masterTile.name != 'Click on a tile!') {
      fetchData();
    } else {
      setPrompt({
        type: 'FeatureCollection',
        features: [{ type: 'FeatureCollection', features: [] }],
      });
    }
  }, [masterTile, redactNFTs]);

  useEffect(() => {
    localStorage.setItem('preview', tilePreview);
  }, [tilePreview, redactNFTs]);

  //Comment out this funtion as well as the one below to remove the zoom out unlock behavior
  useEffect(() => {
    //console.log(updateOnZoom)
    if (tilePreview !== -1 && updateOnZoom == true) {
      //These lines allow you to set the value at which zoom out causes an unlock, currently: 0.5
      //if(map.getZoom() < 2 * currZoom / 3){
      if (currZoom - map.getZoom() > 1.5) {
        setTilePreview(-1);
        //updateOnZoom = false;
        setUpdateOnZoom(false);
      }
    }
  }, [map.getZoom()]);

  //Comment out this funtion to remove the zoom out unlock behavior
  useEffect(() => {
    //updateOnZoom = true;
    setUpdateOnZoom(true);
  }, [currZoom]);
  //console.log(tilePreview)
  return (
    <GeoJSON
      ref={promptOverlayRef}
      key={rand}
      data={promptData.features}
      style={{
        stroke: '#555555',
        fillOpacity: 0,
        opacity: 0,
        weight: 2.75,
        color: config.subtile_hover_color,
        fill: 1,
        fillColor: 'transparent',
        //dashArray: '2 5'
      }}
      onEachFeature={(feature, layer) => {
        if (singleSubregion && subregionSelected) {
          selectedSubregion = [layer, feature.properties.img_prompt];
        }
        if (
          feature.properties.img_prompt == selectedSubregion[1] &&
          subregionMode == parseInt(getNum(masterTile.masterTile.name))
        ) {
          layer.setStyle({
            opacity: 1 * allOverlays,
            color: config.subtile_select_color,
            weight: 3,
          });
          subregionSelected = true;
        } else {
          if (isMobile || isTablet) {
            layer.setStyle({
              color: 'transparent',
            });
          }
        }
        layer.bringToBack();
        if (currentPrompt != null) {
          if (!(isMobile || isTablet)) {
            if (feature.properties.img_prompt == currentPrompt[1]) {
              layer.setStyle({
                opacity: 1 * allOverlays,
              });
            }
          }
        }

        layer.on({
          click: () => {
            let parentTileTemp = parseInt(getNum(masterTile.masterTile.name));
            if (
              tilePreview == parentTileTemp ||
              isMobile ||
              isTablet ||
              redactNFTs
            ) {
              selectedSubregion = [layer, feature.properties.img_prompt];
              if (subregionMode == -1) {
                // || (feature.properties.img_prompt != currentPrompt[1])
                setCurrentPrompt([
                  feature.id,
                  feature.properties.img_prompt,
                  feature.geometry,
                  feature.properties.wiki,
                ]);
                setSingleRegion(false);
                setSubregionMode(parentNumber);

                var urlId =
                  '/todem/' +
                  parentNumber.toString().padStart(4, '0') +
                  '.' +
                  feature.id.toString().padStart(4, '0');
                window.history.pushState('', '', '' + urlId);

                if (DetectDevice) {
                  const promptContainer =
                    document.getElementsByClassName('grid-item-3')[0];
                  if (promptContainer) {
                    promptContainer.style.visibility = 'visible';
                    promptContainer.style.display = 'block';
                  }

                  map.touchZoom.disable();
                  map.dragging.disable();
                }

                zoomToLayer(map, feature, 1);
              } else {
                var urlId =
                  '/todem/' + parentNumber.toString().padStart(4, '0');
                window.history.pushState('', '', '' + urlId);

                if (singleSubregion) {
                  zoomToLayer(map, feature, 1);
                } else {
                  zoomToLayer(
                    map,
                    polygonJson.features[0].features[subregionMode].features[0],
                    1
                  );
                }
                selectedSubregion = [null, null];
                subregionSelected = false;
                setSubregionMode(-1);

                if (isMobile || isTablet) {
                  layer.setStyle({
                    opacity: 0,
                    color: 'transparent',
                  });
                }
              }
            } else {
              var urlId =
                '/todem/' + parentTileTemp.toString().padStart(4, '0');
              window.history.pushState('', '', '' + urlId);
              setTilePreview(parentTileTemp);
              zoomToLayer(
                map,
                polygonJson.features[0].features[parentTileTemp].features[0],
                1
              );
            }
          },
          mouseover: () => {
            if (!DetectDevice) {
              if (subregionMode == -1) {
                setSingleRegion(false);
                setCurrentPrompt([
                  feature.id,
                  feature.properties.img_prompt,
                  feature.geometry,
                  feature.properties.wiki,
                ]);
                selectedSubregion = [null, null];
                subregionSelected = false;

                layer.setStyle({
                  opacity: 1 * allOverlays,
                });
                layer.bringToFront();
              }

              if (selectedLayer != null) {
                selectedLayer.setStyle({
                  opacity: 1 * allOverlays,
                  fillOpacity: 0,
                  color: config.tile_select_color,
                  weight: 3,
                });
              }
            }
          },

          mouseout: () => {
            //setSingleRegion(true)
            layer.setStyle({ opacity: 0 });
            if (feature.properties.img_prompt == selectedSubregion[1]) {
              layer.setStyle({ opacity: 1 * allOverlays });
              if (selectedLayer != null) {
                selectedLayer.setStyle({
                  opacity: 1 * allOverlays,
                  fillOpacity: 0,
                  color: config.tile_select_color,
                  weight: 3,
                });
              } else {
                layer.setStyle({ opacity: 0 });
              }

              clearTimeout(my_time);
              clearTimeout(startScrolling);
              var objDiv = document.getElementById('scrolling-prompt');
              objDiv.scrollTop = 0;
            }
          },
        });
      }}
    />
  );
};

/**
 * Component to display the basic overlay (just grid)
 */
export const BasicOverlay = React.memo(
  ({
    gridOverlay,
    sidebarState,
    setSidebarTitle,
    setSidebar,
    setMasterTile,
    allOverlays,
    popout,
    setCurrentPrompt,
    setSingleRegion,
    tilePreview,
    setTilePreview,
    redactNFTs,
  }) => {
    const map = useMap();
    var video_path = '';
    useEffect(() => {
      if (sidebarState == 0 && popout == 0) {
        selectedLayer = null;
      }
    }, [sidebarState]);

    useEffect(() => {
      if (selectedLayer != null) {
        var queryNumObject = {
          number: selectedLayer.feature.properties.name.split('-')[1],
        };
        let shouldZoom = 0;
        selectQueried({
          map,
          gridOverlay,
          sidebarState,
          setMasterTile,
          setSidebarTitle,
          shouldZoom,
          queryNumObject,
          setTilePreview,
        });
      }
    }, []);

    useEffect(() => {
      if (selectedLayer != null) {
        selectedLayer.setStyle({
          opacity: 1 * allOverlays,
          color: config.tile_select_color,
          weight: 3,
        });
      }
      sidebar = sidebarState;
    });

    sidebar = sidebarState;
    mouseover_opacity = 0.75 * allOverlays;

    useEffect(() => {
      localStorage.setItem('preview', tilePreview);
    }, [tilePreview]);

    return (
      <GeoJSON
        ref={gridOverlay}
        opacity={0}
        data={polygonJson.features}
        style={{
          fillOpacity: 0,
          opacity: config.overlay_opacity * allOverlays,
          weight: 2,
          color: config.tile_default_color,
        }}
        onEachFeature={(feature, layer) => {
          layer.on({
            mouseover: (event) => {
              if (!isMobile && !isTablet)
                var promptContainer =
                  document.getElementsByClassName('prompt-container')[0];
              if (selectedLayer !== layer) {
                layer.setStyle({
                  fillOpacity: 0,
                  opacity: mouseover_opacity,
                  color: config.tile_hover_color,
                });

                if (promptContainer && !subregionSelected) {
                  promptContainer.style.display = 'none';
                }

                if (!subregionSelected && selectedLayer != layer) {
                  setCurrentPrompt(null);
                  setSingleRegion(false);
                } /*
            if (selectedLayer !== layer) {
              setCurrentPrompt(null)
              setSingleRegion(false)
            }*/

                if (
                  !redactNFTs &&
                  localStorage.getItem('preview') == -1 &&
                  !isMobile &&
                  !isTablet
                ) {
                  setSidebar(1);
                  //setSubregionMode(0);
                  setSelectedSubregionNull();
                  var newMasterTile = {
                    masterTile: {
                      name: feature.properties.name,
                      tokenId: feature.properties.tokenId,
                      sentimentX_token: feature.properties.sentimentX_token,
                      sentimentY_token: feature.properties.sentimentY_token,
                      img_prompt: feature.properties.img_prompt,
                      wiki_token: feature.properties.wiki_token,
                      rgb: feature.properties.rgb,
                      dimensions: getDimensions(feature.geometry),
                    },
                  };
                  setMasterTile(newMasterTile);
                  setSidebarTitle(feature.properties.tokenId);
                  var video = document.getElementById('video');

                  video_path = config.path_to_videos.concat(
                    feature.properties.name,
                    '.mp4'
                  );
                  if (video) {
                    video.setAttribute('src', video_path);
                  }

                  event.target.bringToFront();
                  selectedLayer = layer;
                }
              }
            },
            mouseout: (event) => {
              if (selectedLayer !== layer) {
                layer.setStyle({
                  fillOpacity: 0,
                  opacity: config.overlay_opacity,
                  color: config.tile_default_color,
                });
                layer.setStyle({
                  className: null, // what does this do??
                });
              }
            },
            click: (event) => {
              setSidebar(1);
              let parentTileTemp = parseInt(getNum(feature.properties.name));
              setTilePreview(parentTileTemp);
              setSelectedSubregionNull();
              var newMasterTile = {
                masterTile: {
                  name: feature.properties.name,
                  tokenId: feature.properties.tokenId,
                  sentimentX_token: feature.properties.sentimentX_token,
                  sentimentY_token: feature.properties.sentimentY_token,
                  img_prompt: feature.properties.img_prompt,
                  wiki_token: feature.properties.wiki_token,
                  rgb: feature.properties.rgb,
                  dimensions: getDimensions(feature.geometry),
                },
              };

              setMasterTile(newMasterTile);
              setSidebarTitle(feature.properties.tokenId);
              var video = document.getElementById('video');
              video_path = config.path_to_videos.concat(
                feature.properties.name,
                '.mp4'
              );
              if (video) {
                video.setAttribute('src', video_path);
              }

              event.target.bringToFront();

              layer.setStyle({
                opacity: 1 * allOverlays,
                fillOpacity: 0,
                color: config.tile_select_color,
                weight: 3,
              });
              selectedLayer = layer;
              var urlId = '/todem/' + getNum(feature.properties.name);
              window.history.pushState('', '', '' + urlId);
              zoomToLayer(map, feature, sidebarState, setSidebar);
            },
          });
        }}
      />
    );
  }
);

/**
 * Component to display the Availability overlay (Add color based on ownership status)
 */
export const AvailibilityOverlay = React.memo(
  ({
    gridOverlay,
    sidebarState,
    setSidebarTitle,
    setSidebar,
    setMasterTile,
    allOverlays,
    popout,
    setCurrentPrompt,
    setSingleRegion,
    tilePreview,
    setTilePreview,
    redactNFTs,
    ownership,
  }) => {
    const map = useMap();
    const blockchain = useSelector((state) => state.blockchain);

    var video_path = '';
    useEffect(() => {
      if (sidebarState == 0 && popout == 0) {
        selectedLayer = null;
      }
    }, [sidebarState]);

    useEffect(() => {
      if (selectedLayer != null) {
        var queryNumObject = {
          number: selectedLayer.feature.properties.name.split('-')[1],
        };
        let shouldZoom = 0;
        selectQueried({
          map,
          gridOverlay,
          sidebarState,
          setMasterTile,
          setSidebarTitle,
          shouldZoom,
          queryNumObject,
          setTilePreview,
        });
      }
    }, []);

    useEffect(() => {
      if (selectedLayer != null) {
        selectedLayer.setStyle({
          opacity: 1 * allOverlays,
          fillOpacity: 0,
          color: config.tile_select_color,
          weight: 3,
        });
      }
      sidebar = sidebarState;
    });

    sidebar = sidebarState;
    overlay_opacity = config.overlay_opacity * allOverlays;
    fill_overlay_opacity = 1 * allOverlays;
    mouseover_opacity = 0.75 * allOverlays;

    useEffect(() => {
      localStorage.setItem('preview', tilePreview);
    }, [tilePreview]);

    useEffect(() => {
      //console.log(ownership)
        if (!map || !gridOverlay.current) return;

        const layers = gridOverlay.current.getLayers();
        layers.forEach(layer => {
          const tokenId = layer.feature.properties.tokenId;
          const ownershipStatus = ownership[tokenId];
          let fillColor = '#ffffff80'; // default color

          if (ownershipStatus === 2) {
            fillColor = '#FFB43E'; // owned
          } else if (ownershipStatus === 1) {
            fillColor = '#A7272B'; // in process
          }

          layer.setStyle({
            fillColor: fillColor,
          });
        });
    }, [ownership, map]);

    //old code from when ownership status wasn't synced at the top level
    /*
    const checkOwner = async () => {
      const address = String(contractAddress);
      const opts = {
        pageKey: '1000',
        omitMetadata: false,
        pageSize: 10,
        tokenUriTimeoutInMs: 0,
      };
      let i = 1;
      for await (const nft of alchemy.nft.getNftsForContractIterator(
        address,
        opts
      )) {
        if (ownership[nft.tokenId] === 0) {
          ownership[nft.tokenId] = 1;
        }
        else{
          ownership[nft.tokenId] = 1;
        }
        i++;
      }
    };

    async function fetchCurrUserNFTs(addr) {
      let web3;
      web3 = new Web3(window.ethereum);
      let nfts;
      const currWallet = String(addr); //String(blockchain.account);
      var requestOptions = {
        method: 'GET',
      };

      if (currWallet !== '' && currWallet !== 'null') {
        nfts = await alchemy.nft.getNftsForOwner(currWallet);
      }
      if (nfts) {
        for (let nft of nfts.ownedNfts) {
          if (
            nft.contract.address.toLowerCase() === contractAddress.toLowerCase()
          ) {
            ownership[nft.tokenId] = 2;
          }
        }
      }
    }

    useEffect(() => {
      // Clear any existing timeout to avoid memory leaks or unintended behavior
      checkOwner().then(() => {
      fetchCurrUserNFTs(blockchain.account).then(() => {
        if (!map || !gridOverlay.current) return;

        const layers = gridOverlay.current.getLayers();
        layers.forEach(layer => {
          const tokenId = layer.feature.properties.tokenId;
          const ownershipStatus = ownership[tokenId];
          let fillColor = '#ffffff80'; // default color

          if (ownershipStatus === 2) {
            fillColor = '#FFB43E'; // owned
          } else if (ownershipStatus === 1) {
            fillColor = '#A7272B'; // in process
          }

          layer.setStyle({
            fillColor: fillColor,
          });
        });
      });
      });
    }, [blockchain.account, map]); // Depend on ownership and map
    // Depend on ownership and map
    */
    return (
      <GeoJSON
        ref={gridOverlay}
        opacity={0}
        data={polygonJson.features}
        style={{
          fillOpacity: fill_overlay_opacity,
          opacity: overlay_opacity,
          weight: 2,
          color: config.tile_default_color,
        }}
        onEachFeature={(feature, layer) => {
          //ownedStatus(feature.properties.tokenId);
          if (ownership[feature.properties.tokenId] == 2) {
            layer.setStyle({
              fillColor: '#FFB43E',
            });
          } else if (ownership[feature.properties.tokenId] == 1) {
            layer.setStyle({
              fillColor: '#A7272B',
            });
          } else {
            layer.setStyle({
              fillColor: '#ffffff80',
            });
          }

          layer.on({
            mouseover: (event) => {
              var promptContainer =
                document.getElementsByClassName('prompt-container')[0];
              if (selectedLayer !== layer) {
                layer.setStyle({
                  fillOpacity: 0.55 * fill_overlay_opacity,
                  opacity: overlay_opacity,
                  color: config.tile_hover_color,
                });
                if (promptContainer && !subregionSelected) {
                  promptContainer.style.display = 'none';
                }

                // A workaround for hovered tiles sometimes turning off after mouse out
                if (!subregionSelected && selectedLayer != layer) {
                  setCurrentPrompt(null);
                  setSingleRegion(false);
                }
                /*
           if (selectedLayer != layer) {
             setCurrentPrompt(null)
             setSingleRegion(false)
           }*/

                if (
                  !redactNFTs &&
                  localStorage.getItem('preview') == -1 &&
                  !isMobile &&
                  !isTablet
                ) {
                  setSidebar(1);
                  //setSubregionMode(0);
                  setSelectedSubregionNull();
                  var newMasterTile = {
                    masterTile: {
                      name: feature.properties.name,
                      tokenId: feature.properties.tokenId,
                      sentimentX_token: feature.properties.sentimentX_token,
                      sentimentY_token: feature.properties.sentimentY_token,
                      img_prompt: feature.properties.img_prompt,
                      wiki_token: feature.properties.wiki_token,
                      rgb: feature.properties.rgb,
                      dimensions: getDimensions(feature.geometry),
                    },
                  };
                  setMasterTile(newMasterTile);
                  setSidebarTitle(feature.properties.tokenId);
                  var video = document.getElementById('video');

                  video_path = config.path_to_videos.concat(
                    feature.properties.name,
                    '.mp4'
                  );
                  if (video) {
                    video.setAttribute('src', video_path);
                  }

                  event.target.bringToFront();
                  selectedLayer = layer;
                }
              }
            },

            mouseout: (event) => {
              if (selectedLayer !== layer) {
                layer.setStyle({
                  fillOpacity: fill_overlay_opacity,
                  opacity: overlay_opacity,
                  color: config.tile_default_color,
                });
                layer.setStyle({
                  className: null,
                });
              }
            },
            click: (event) => {
              setSidebar(1);
              let parentTileTemp = parseInt(getNum(feature.properties.name));
              setTilePreview(parentTileTemp);
              setSelectedSubregionNull();
              var newMasterTile = {
                masterTile: {
                  name: feature.properties.name,
                  tokenId: feature.properties.tokenId,
                  sentimentX_token: feature.properties.sentimentX_token,
                  sentimentY_token: feature.properties.sentimentY_token,
                  img_prompt: feature.properties.img_prompt,
                  wiki_token: feature.properties.wiki_token,
                  rgb: feature.properties.rgb,
                  dimensions: getDimensions(feature.geometry),
                },
              };

              setMasterTile(newMasterTile);
              setSidebarTitle(feature.properties.tokenId);
              var video = document.getElementById('video');
              video_path = config.path_to_videos.concat(
                feature.properties.name,
                '.mp4'
              );
              if (video) {
                video.setAttribute('src', video_path);
              }

              event.target.bringToFront();

              layer.setStyle({
                opacity: 1 * allOverlays,
                fillOpacity: 0,
                color: config.tile_select_color,
                weight: 3,
              });

              selectedLayer = layer;
              var urlId = '/todem/' + getNum(feature.properties.name);
              window.history.pushState('', '', '' + urlId);
              zoomToLayer(map, feature, sidebarState, setSidebar);
            },
          });
        }}
      />
    );
  }
);

/**
 * Component to display the class overlay 
 */
export const ClassOverlay = React.memo(
  ({
    gridOverlay,
    sidebarState,
    setSidebarTitle,
    setSidebar,
    setMasterTile,
    allOverlays,
    popout,
    setCurrentPrompt,
    setSingleRegion,
    tilePreview,
    setTilePreview,
    redactNFTs,
  }) => {
    const map = useMap();
    var video_path = '';
    useEffect(() => {
      if (sidebarState == 0 && popout == 0) {
        selectedLayer = null;
      }
    }, [sidebarState]);

    useEffect(() => {
      if (selectedLayer != null) {
        var queryNumObject = {
          number: selectedLayer.feature.properties.name.split('-')[1],
        };
        let shouldZoom = 0;
        selectQueried({
          map,
          gridOverlay,
          sidebarState,
          setMasterTile,
          setSidebarTitle,
          shouldZoom,
          queryNumObject,
          setTilePreview,
        });
      }
    }, []);

    useEffect(() => {
      if (selectedLayer != null) {
        selectedLayer.setStyle({
          opacity: 1 * allOverlays,
          fillOpacity: 0,
          color: config.tile_select_color,
          weight: 3,
        });
      }
      sidebar = sidebarState;
    });

    sidebar = sidebarState;
    overlay_opacity = config.overlay_opacity * allOverlays;
    fill_overlay_opacity = 1 * allOverlays;
    mouseover_opacity = 0.75 * allOverlays;

    useEffect(() => {
      localStorage.setItem('preview', tilePreview);
    }, [tilePreview]);

    return (
      <GeoJSON
        ref={gridOverlay}
        opacity={0}
        data={polygonJson.features}
        style={{
          fillOpacity: fill_overlay_opacity,
          opacity: overlay_opacity,
          weight: 2,
          color: config.tile_default_color,
        }}
        onEachFeature={(feature, layer) => {
          if (feature.properties.name[5] == 'A') {
            layer.setStyle({
              fillColor: '#ff6a6ad9',
            });
          } else if (feature.properties.name[5] == 'B') {
            layer.setStyle({
              fillColor: '#ffff9bb7',
              // fillColor: "#ffbe00bf"
            });
          } else if (feature.properties.name[5] == 'C') {
            layer.setStyle({
              fillColor: '#649bffb7',
            });
          } else {
            layer.setStyle({
              fillColor: '#ffffffb7',
            });
          }

          layer.on({
            mouseover: (event) => {
              var promptContainer =
                document.getElementsByClassName('prompt-container')[0];
              if (selectedLayer !== layer) {
                layer.setStyle({
                  fillOpacity: 0.55 * fill_overlay_opacity,
                  opacity: mouseover_opacity,
                  color: config.tile_hover_color,
                });
                if (promptContainer && !subregionSelected) {
                  promptContainer.style.display = 'none';
                }
              }
              if (!subregionSelected && selectedLayer != layer) {
                setCurrentPrompt(null);
                setSingleRegion(false);
              }
              /*
          if (selectedLayer != layer) {
            setCurrentPrompt(null)
            setSingleRegion(false)
          }*/

              if (
                !redactNFTs &&
                localStorage.getItem('preview') == -1 &&
                !isMobile &&
                !isTablet
              ) {
                setSidebar(1);
                //setSubregionMode(0);
                setSelectedSubregionNull();
                var newMasterTile = {
                  masterTile: {
                    name: feature.properties.name,
                    tokenId: feature.properties.tokenId,
                    sentimentX_token: feature.properties.sentimentX_token,
                    sentimentY_token: feature.properties.sentimentY_token,
                    img_prompt: feature.properties.img_prompt,
                    wiki_token: feature.properties.wiki_token,
                    rgb: feature.properties.rgb,
                    dimensions: getDimensions(feature.geometry),
                  },
                };
                setMasterTile(newMasterTile);
                setSidebarTitle(feature.properties.tokenId);
                var video = document.getElementById('video');

                video_path = config.path_to_videos.concat(
                  feature.properties.name,
                  '.mp4'
                );
                if (video) {
                  video.setAttribute('src', video_path);
                }

                event.target.bringToFront();
                selectedLayer = layer;
              }
            },
            mouseout: (event) => {
              if (selectedLayer !== layer) {
                layer.setStyle({
                  fillOpacity: fill_overlay_opacity,
                  opacity: overlay_opacity,
                  color: config.tile_default_color,
                });
                layer.setStyle({
                  className: null,
                });
              }
            },
            click: (event) => {
              setSidebar(1);
              let parentTileTemp = parseInt(getNum(feature.properties.name));
              setTilePreview(parentTileTemp);
              setSelectedSubregionNull();
              var newMasterTile = {
                masterTile: {
                  name: feature.properties.name,
                  tokenId: feature.properties.tokenId,
                  sentimentX_token: feature.properties.sentimentX_token,
                  sentimentY_token: feature.properties.sentimentY_token,
                  img_prompt: feature.properties.img_prompt,
                  wiki_token: feature.properties.wiki_token,
                  rgb: feature.properties.rgb,
                  dimensions: getDimensions(feature.geometry),
                },
              };

              setMasterTile(newMasterTile);
              setSidebarTitle(feature.properties.tokenId);
              var video = document.getElementById('video');
              video_path = config.path_to_videos.concat(
                feature.properties.name,
                '.mp4'
              );
              if (video) {
                video.setAttribute('src', video_path);
              }

              event.target.bringToFront();

              layer.setStyle({
                opacity: 1 * allOverlays,
                fillOpacity: 0,
                color: config.tile_select_color,
                weight: 3,
              });
              selectedLayer = layer;
              var urlId = '/todem/' + getNum(feature.properties.name);
              window.history.pushState('', '', '' + urlId);
              zoomToLayer(map, feature, sidebarState, setSidebar);
            },
          });
        }}
      />
    );
  }
);

/**
 * Component to display no overlay
 */
export const NoOverlay = React.memo(
  ({
    gridOverlay,
    sidebarState,
    setSidebarTitle,
    setSidebar,
    setMasterTile,
    allOverlays,
    popout,
    setCurrentPrompt,
    setSingleRegion,
    tilePreview,
    setTilePreview,
    setSubregionMode,
    redactNFTs,
  }) => {
    const map = useMap();
    var video_path = '';
    //selectedLayer = null;
    useEffect(() => {
      if (sidebarState == 0 && popout == 0) {
        selectedLayer = null;
      }
    }, [sidebarState]);

    useEffect(() => {
      if (selectedLayer != null) {
        var queryNumObject = {
          number: selectedLayer.feature.properties.name.split('-')[1],
        };
        let shouldZoom = 0;
        selectQueried({
          map,
          gridOverlay,
          sidebarState,
          setMasterTile,
          setSidebarTitle,
          shouldZoom,
          queryNumObject,
          setTilePreview,
        });
      }
    }, []);

    useEffect(() => {
      if (selectedLayer != null) {
        selectedLayer.setStyle({
          opacity: 1 * allOverlays,
          fillOpacity: 0,
          color: config.tile_select_color,
          weight: 3,
        });
      }
      sidebar = sidebarState;
    });

    sidebar = sidebarState;
    mouseover_opacity = 0.75 * allOverlays;

    useEffect(() => {
      localStorage.setItem('preview', tilePreview);
    }, [tilePreview]);
    return (
      <GeoJSON
        ref={gridOverlay}
        opacity={0}
        data={polygonJson.features}
        style={{
          fillOpacity: 0,
          opacity: 0,
          weight: 2,
          color: config.tile_hover_color,
        }}
        onEachFeature={(feature, layer) => {
          layer.on({
            mouseover: (event) => {
              var promptContainer =
                document.getElementsByClassName('prompt-container')[0];
              if (selectedLayer != layer) {
                layer.setStyle({
                  opacity: mouseover_opacity,
                });
                if (promptContainer && !subregionSelected) {
                  promptContainer.style.display = 'none';
                }
                // A workaround for hovered tiles sometimes turning off after mouse out

                if (!subregionSelected && selectedLayer != layer) {
                  setCurrentPrompt(null);
                  setSingleRegion(false);
                }
                /*
            if (selectedLayer != layer) {
              setCurrentPrompt(null)
              setSingleRegion(false)
            }*/
                //console.log(localStorage.getItem("preview"))
                if (
                  !redactNFTs &&
                  localStorage.getItem('preview') == -1 &&
                  !isMobile &&
                  !isTablet
                ) {
                  setSidebar(1);
                  //setSubregionMode(-1);
                  //setCurrentPrompt([feature.id, feature.properties.img_prompt, feature.geometry, feature.properties.wiki])
                  setSelectedSubregionNull();
                  var newMasterTile = {
                    masterTile: {
                      name: feature.properties.name,
                      tokenId: feature.properties.tokenId,
                      sentimentX_token: feature.properties.sentimentX_token,
                      sentimentY_token: feature.properties.sentimentY_token,
                      img_prompt: feature.properties.img_prompt,
                      wiki_token: feature.properties.wiki_token,
                      rgb: feature.properties.rgb,
                      dimensions: getDimensions(feature.geometry),
                    },
                  };
                  setMasterTile(newMasterTile);
                  setSidebarTitle(feature.properties.tokenId);

                  var video = document.getElementById('video');

                  video_path = config.path_to_videos.concat(
                    feature.properties.name,
                    '.mp4'
                  );
                  if (video) {
                    video.setAttribute('src', video_path);
                  }

                  event.target.bringToFront();
                  selectedLayer = layer;
                }
              }
            },
            mouseout: (event) => {
              if (selectedLayer !== layer) {
                layer.setStyle({
                  opacity: 0,
                });
                layer.setStyle({
                  className: null,
                });
              }
            },
            click: (event) => {
              setSidebar(1);
              let parentTileTemp = parseInt(getNum(feature.properties.name));
              setTilePreview(parentTileTemp);
              setSelectedSubregionNull();
              var newMasterTile = {
                masterTile: {
                  name: feature.properties.name,
                  tokenId: feature.properties.tokenId,
                  sentimentX_token: feature.properties.sentimentX_token,
                  sentimentY_token: feature.properties.sentimentY_token,
                  img_prompt: feature.properties.img_prompt,
                  wiki_token: feature.properties.wiki_token,
                  rgb: feature.properties.rgb,
                  dimensions: getDimensions(feature.geometry),
                },
              };

              setMasterTile(newMasterTile);
              setSidebarTitle(feature.properties.tokenId);
              var video = document.getElementById('video');
              video_path = config.path_to_videos.concat(
                feature.properties.name,
                '.mp4'
              );
              if (video) {
                video.setAttribute('src', video_path);
              }

              event.target.bringToFront();

              layer.setStyle({
                opacity: 1 * allOverlays,
                color: config.tile_select_color,
                weight: 3,
              });

              selectedLayer = layer;
              var urlId = '/todem/' + getNum(feature.properties.name);
              window.history.pushState('', '', '' + urlId);
              zoomToLayer(map, feature, sidebarState, setSidebar);
            },
          });
        }}
      />
    );
  }
);

// Returns the zoom layer that fills the screen with the
export function fillScreen() {
  let h = Math.max(
    document.documentElement.clientHeight,
    window.innerHeight || 0
  );
  let w = Math.max(
    document.documentElement.clientWidth,
    window.innerWidth || 0
  );

  let map_lat = config.map_width / Math.pow(2, config.max_native_zoom);
  let map_lng = config.map_height / Math.pow(2, config.max_native_zoom);

  let screenratio = w / (h - 40);
  let mapratio = map_lat / map_lng;
  let tall = mapratio < screenratio;

  let zoom = Math.log2(w / (1.1 * map_lat));

  if (tall) {
    zoom = Math.log2((h - 40) / (1.1 * map_lng));
  }
  return zoom;
}

export function calculateCenter() {
  return [
    -1 * (config.map_height / Math.pow(2, config.max_native_zoom + 1)),
    config.map_width / Math.pow(2, config.max_native_zoom + 1),
  ];
}

function mobileClearVideo() {
  var video = document.getElementsByClassName('grid-item-2')[0];
  video.style.visibility = 'hidden';
  video.style.display = 'none';
}

function calculateTileBounds() {
  return [
    -1 * (config.map_height / Math.pow(2, config.max_native_zoom)),
    config.map_width / Math.pow(2, config.max_native_zoom),
  ];
}

// Tiler layer with the webp components
function MainTileLayer({
  mainTL,
  setAnimationStartTime,
  sync_flag,
  isPaused,
  renderNumber,
}) {
  const map = useMap();

  // Helper function for syncing video with wepbs
  function resetVideo() {
    let video = document.getElementById('sidebar-video');
    if (video != undefined) {
      if (video.src.slice(-3) === 'mp4') {
        function playVideo() {
          video.pause();
          video.currentTime = 0;
          var playPromise = video.play();

          if (playPromise !== undefined) {
            playPromise
              .then((_) => {
                // Automatic playback started!
                // Show playing UI.
              })
              .catch((error) => {
                // Auto-play was prevented
                // Show paused UI.
              });
          }
        }

        // Remove old event listener, if it exists
        if (video.onloaddata) {
          video.onloaddata = null;
        }

        if (video.readyState > 2) {
          // If enough data is available to at least start playing
          playVideo();
        } else {
          // If not enough data is available, wait for it
          video.onloaddata = playVideo;
        }
      }
    }
  }

  return (
    <TileLayer
      ref={mainTL}
      className="main-tile-layer"
      key={`webp-${renderNumber}`}
      url={`https://www.latentculture.com/tohm/render_${renderNumber}/map/webp/{z}/{x}-{y}.webp`}
      noWrap={true}
      tileSize={config.tile_size}
      updateWhenZooming={false}
      updateInterval={1000}
      updateWhenIdle={true}
      keepBuffer={0}
      minZoom={config.min_zoom_layer + 2 * !DetectDevice - 1}
      maxZoom={config.max_zoom_layer}
      minNativeZoom={config.min_native_zoom}
      maxNativeZoom={config.max_native_zoom}
      zIndex={5}
      opacity={1}
      bounds={[[0, 0], calculateTileBounds()]}
      /* 
      THIS LOGIC IS USED TO SYNC WEBP ANIMATIONS
      sync_flag is a flag that is set to 1 when a sync is occuring
      */

      eventHandlers={{
        // When any loading call is made
        loading: (e) => {
          // If no ongoing sync is occuring for particular render
          if (sync_flag.current != renderNumber) {
            sync_flag.current = renderNumber;

            // Play loading animation
            let loading = document.getElementsByClassName('loading')[0];
            if (loading) {
              loading.style.display = 'flex';
              loading.style.visibility = 'visible';
            }

            // Hide playbox on mobile
            let playbox = document.getElementsByClassName(
              'bttn frame-controller'
            )[0];

            if (splashStillUp && DetectDevice) {
              if (loading) {
                // loading.style.display = "none";
                // loading.style.visibility = "hidden";
              }
              playbox.style.visibility = 'hidden';
            }

            // This seems to be a workaround to check if the user has clicked off the splash screen
            let projTitle = document.getElementsByClassName('project-title')[0];
            if (null != projTitle && projTitle.style.visibility == 'visible') {
              mainTL.current.setOpacity(0);
            }

            // Idea is that we wait for all times to load and then re-add them to map
            // However, for whatever reason, it doesn't sync unless  we do this twice
            mainTL.current.once({
              load: (event) => {
                mainTL.current.remove();
                mainTL.current.addTo(map);

                mainTL.current.once({
                  load: (event) => {
                    mainTL.current.remove();
                    mainTL.current.addTo(map);
                    mainTL.current.once({
                      // Once all tiles are loaded and synced we can play through
                      load: (event) => {
                        sync_flag.current = 0;
                        if (loading) {
                          loading.style.display = 'none';
                          loading.style.visibility = 'hidden';
                        }
                        if (!isMouseDown && !isPaused) {
                          mainTL.current.setOpacity(1);
                        }
                        setTimeout(resetVideo(), 100);
                        setAnimationStartTime(Date.now());
                      },
                    });
                  },
                });
              },
            });
          }
        },
      }}
    />
  );
}

function MyMap({ handle, skipSplash, mintingOverride, showVersionToggle }) {
  const dispatch = useDispatch();

  //THE state variables
  //set the initial render number (14, 42, or 56)
  const [renderNumber, setRenderNumber] = useState(56);
  //object to load all blockchain data relating to the TODEM NFT contract and connected account
  const blockchain = useSelector((state) => state.blockchain);
  // Controls whether or not info panel being displayged
  const [projectInfo, setProjectInfo] = useState(0); 
  // Helps other components make rendering choices
  const [sidebarState, setSidebar] = useState(0);
  // Name of the sidebar, also Tile name
  const [sidebarTitle, setSidebarTitle] = useState('Click a tile!');
  //This seems unused
  const [selectedLayerState, setSelectedLayer] = useState(selectedLayer);
  //Set the popout as open or closed
  const [popout, setPopout] = useState(0); // Controls whether or not popout panel being displayed
  //Sets prompt to be displayed in sidebar, used in determining subregion selection
  const [currentPrompt, setCurrentPrompt] = useState(null);
  // Overlay management
  const [allOverlays, setAllOverlays] = useState(1);
  // Overlay management
  const [overlayMode, setOverlayMode] = useState(3);
  // Overlay management
  const [oldOverlay, setOldOverlay] = useState(-1);
  // Setting selected subregion. Useful in subtile preview (light blue) vs. subtile selected (dark blue).
  const [subregionMode, setSubregionMode] = useState(-1);
  // display mint message of some type
  const [mintMessage, setMintMessage] = useState(null);
  // add custom text to mint message
  const [revertMessage, setRevertMessage] = useState(null);
  // Used to manage subregion logic for the case where the Tile only has one subregion
  const [singleRegion, setSingleRegion] = useState(false);
  // Sets frame number of animation
  const [frameNum, setFrameNum] = useState(0);
  // Sets frame number of animation
  const [leadingFrameNum, setLeadingFrameNum] = useState(-1);
  // Sets animation to paused
  const [isPaused, setIsPaused] = useState(false);
  const [animationStartTime, setAnimationStartTime] = useState(null);
  // Used to manage subregion logic
  const [tilePreview, setTilePreview] = useState(-1);
  // Used to set Zoom level
  const [currZoom, setCurrZoom] = useState(-3);
  // Used to set Zoom level
  const [updateOnZoom, setUpdateOnZoom] = useState(false);
  // Used to change from listview to pricing in info
  const [listViewExpanded, setlistViewExpanded] = useState(0);
  // Used to Open the listview
  const [listView, setListView] = useState(0);
  // Used to signal when ownership is updated
  const [ownershipUpdated, setOwnershipUpdated] = useState(false);
  // Used to signal is physical has been claimed
  const [physicalClaimed, setphysicalClaimed] = useState(false);
  // Used to signal is minting is paused
  const [mintingPaused, setMintingPaused] = useState(
    0 && mintingOverride != true
  ); // this is the flag to turn minting functionality on and off
  const [showBanner, setShowBanner] = useState(0 && mintingOverride != true); // togggles pre-launch or minting countdown banner
  const [bannerMessage, setBannerMessage] = useState(1); // 0 => pre-launch message, 1 => minting countdown
  const [affiliate, setAffiliate] = useState(
    localStorage.getItem('affiliate') || null
  );

  //THE ownership dictionary. Originally loaded empty from the .json. Updated in topbar with changes synced across all components. 
  const [ownership, setOwnership] = useState(ownership_full);

  //Used to change from web3 mode to art mode
  const [redactNFTs, setRedactNFTs] = useState(
    JSON.parse(localStorage.getItem('art_experience')) ?? false
  );

  const [currFetchingItems, setCurrFetchingItems] = useState(false);

  useEffect(() => {
    if (showVersionToggle) {
      if (showVersionToggle == 14) {
        setRenderNumber(14);
      } else if (showVersionToggle == 56) {
        setRenderNumber(56);
      }
    }
  }, [showVersionToggle]);

  //Sets current selected tile and all properties
  const [masterTile, setMasterTile] = useState({
    masterTile: {
      name: 'Click on a tile!',
      tokenId: 'NaN',
      sentimentX_token: '',
      sentimentY_token: '',
      img_prompt: '',
      wiki_token: '',
      rgb: '',
      dimensions: ['', ''],
    },
  });
  const mainTL = useRef();
  const leadingFrameRef = useRef();

  const gridOverlay = useRef();
  const promptOverlayRef = useRef();
  const sync_flag = useRef(0);

  useEffect(() => {
    const loadMobileCSS = async () => {
      if (isMobile) {
        await import('../styles/topbar_mobile.css');
      }
    };

    loadMobileCSS();

    if (skipSplash) {
      splashStillUp = false;
    }
  }, []);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') {
      console.log = () => {};
      console.error = () => {};
      console.warn = () => {};
      console.info = () => {};
    }
  }, []);

  useEffect(() => {
    if (overlayMode == 0 && redactNFTs) {
      setOverlayMode(1);
    }
    localStorage.setItem('art_experience', JSON.stringify(redactNFTs));
  }, [redactNFTs, overlayMode]);

  useEffect(() => {
    setOldOverlay(overlayMode);
    setOverlayMode((overlayMode) => (overlayMode === -1 ? oldOverlay : -1));
  }, [redactNFTs]);

  useEffect(() => {
    if (overlayMode == -1) {
      setOverlayMode(3);
    }
  }, [oldOverlay, overlayMode]);

  return (
    <MapContainer
      preferCanvas={true}
      center={calculateCenter()}
      zoom={fillScreen()} // Still snaps
      dragging={false}
      zoomControl={false}
      attributionControl={false}
      scrollWheelZoom={false}
      smoothSensitivity={1.75}
      smoothWheelZoom={false}
      zoomDelta={0.5}
      zoomSnap={0}
      interia={false}
      inertiaDeceleration={2750}
      inertiaMaxSpeed={1500}
      easeLinearity={0.2}
      boxZoom={false}
      keyboard={false}
      fullscreenControl={true}
      minZoom={config.min_zoom_layer + 2 * !DetectDevice}
      maxZoom={config.max_zoom_layer}
      crs={CRS.Simple}
      maxBoundsViscosity={1}
      doubleClickZoom={false}
      zoomAnimationThreshold={10}
    >
      {listView != 0 && !redactNFTs && !DetectDevice && (
        <ListView
          gridOverlay={gridOverlay}
          setMasterTile={setMasterTile}
          setSidebarTitle={setSidebarTitle}
          listView={listView}
          setListView={setListView}
          ownershipUpdated={ownershipUpdated}
          setSidebar={setSidebar}
          masterTile={masterTile}
          setCurrentPrompt={setCurrentPrompt}
          setTilePreview={setTilePreview}
          setlistViewExpanded={setlistViewExpanded}
          setProjectInfo={setProjectInfo}
          ownership={ownership}
          setOwnership={setOwnership}
        />
      )}

      <PromptOverlay
        promptOverlayRef={promptOverlayRef}
        masterTile={masterTile}
        subregionMode={subregionMode}
        setSubregionMode={setSubregionMode}
        currentPrompt={currentPrompt}
        singleRegion={singleRegion}
        setCurrentPrompt={setCurrentPrompt}
        setSingleRegion={setSingleRegion}
        allOverlays={allOverlays}
        tilePreview={tilePreview}
        setTilePreview={setTilePreview}
        currZoom={currZoom}
        setCurrZoom={setCurrZoom}
        updateOnZoom={updateOnZoom}
        setUpdateOnZoom={setUpdateOnZoom}
        redactNFTs={redactNFTs}
      />

      <>
        {overlayMode == 2 ? (
          <BasicOverlay
            id="BasicOverlay"
            setCurrentPrompt={setCurrentPrompt}
            setSingleRegion={setSingleRegion}
            gridOverlay={gridOverlay}
            sidebarState={sidebarState}
            selectedLayerState={selectedLayerState}
            sidebarTitle={sidebarTitle}
            setSidebarTitle={setSidebarTitle}
            setSidebar={setSidebar}
            mainTL={mainTL}
            masterTile={masterTile}
            setMasterTile={setMasterTile}
            allOverlays={allOverlays}
            popout={popout}
            tilePreview={tilePreview}
            setTilePreview={setTilePreview}
            redactNFTs={redactNFTs}
          />
        ) : null}
      </>

      <>
        {overlayMode == 0 ? (
          <Provider store={store}>
          <AvailibilityOverlay
            setCurrentPrompt={setCurrentPrompt}
            setSingleRegion={setSingleRegion}
            gridOverlay={gridOverlay}
            sidebarState={sidebarState}
            selectedLayerState={selectedLayerState}
            sidebarTitle={sidebarTitle}
            setSidebarTitle={setSidebarTitle}
            setSidebar={setSidebar}
            mainTL={mainTL}
            masterTile={masterTile}
            setMasterTile={setMasterTile}
            allOverlays={allOverlays}
            popout={popout}
            tilePreview={tilePreview}
            setTilePreview={setTilePreview}
            redactNFTs={redactNFTs}
            ownership={ownership}
          />
          </Provider>
        ) : null}
      </>

      <>
        {overlayMode == 1 ? (
          <ClassOverlay
            setCurrentPrompt={setCurrentPrompt}
            setSingleRegion={setSingleRegion}
            gridOverlay={gridOverlay}
            sidebarState={sidebarState}
            selectedLayerState={selectedLayerState}
            sidebarTitle={sidebarTitle}
            setSidebarTitle={setSidebarTitle}
            setSidebar={setSidebar}
            mainTL={mainTL}
            masterTile={masterTile}
            setMasterTile={setMasterTile}
            allOverlays={allOverlays}
            popout={popout}
            tilePreview={tilePreview}
            setTilePreview={setTilePreview}
            redactNFTs={redactNFTs}
          />
        ) : null}{' '}
      </>

      <>
        {overlayMode == 3 ? (
          <NoOverlay
            setCurrentPrompt={setCurrentPrompt}
            setSingleRegion={setSingleRegion}
            gridOverlay={gridOverlay}
            sidebarState={sidebarState}
            selectedLayerState={selectedLayerState}
            sidebarTitle={sidebarTitle}
            setSidebarTitle={setSidebarTitle}
            setSidebar={setSidebar}
            mainTL={mainTL}
            masterTile={masterTile}
            setMasterTile={setMasterTile}
            allOverlays={allOverlays}
            popout={popout}
            tilePreview={tilePreview}
            setTilePreview={setTilePreview}
            setSubregionMode={setSubregionMode}
            redactNFTs={redactNFTs}
          />
        ) : null}
      </>

      <MapEvents
        mainTL={mainTL}
        allOverlays={allOverlays}
        setAllOverlays={setAllOverlays}
        setSidebar={setSidebar}
        subregionMode={subregionMode}
        sync_flag={sync_flag}
        setFrameNum={setFrameNum}
        animationStartTime={animationStartTime}
        isPaused={isPaused}
        projectInfo={projectInfo}
        setCurrZoom={setCurrZoom}
        currZoom={currZoom}
        setUpdateOnZoom={setUpdateOnZoom}
        tilePreview={tilePreview}
        renderNumber={renderNumber}
      />

      {leadingFrameNum != -1 ? (
        <TileLayer
          key={`lagging-${renderNumber}-${leadingFrameNum}`}
          ref={leadingFrameRef}
          url={`https://www.latentculture.com/tohm/render_${renderNumber}/map/jpg${leadingFrameNum}/{z}/{x}-{y}.jpg`}
          noWrap={true}
          tileSize={config.tile_size}
          updateWhenZooming={false}
          updateWhenIdle={true}
          keepBuffer={2}
          minZoom={config.min_zoom_layer + 2 * !DetectDevice - 1}
          maxZoom={config.max_zoom_layer}
          zIndex={2}
          minNativeZoom={1}
          maxNativeZoom={1}
          opacity={1}
          bounds={[[0, 0], calculateTileBounds()]}
          eventHandlers={{
            add: (e) => {
              leadingFrameRef.current.once({
                load: (e) => {
                  if (lagLayerTimeout) {
                    clearTimeout(lagLayerTimeout);
                  }
                  lagLayerTimeout = setTimeout(() => {
                    setFrameNum(leadingFrameNum);
                  }, 165);
                },
              });
            },
          }}
        />
      ) : null}

      {/* 3rd low res layer JS */}
      <TileLayer
        key={`first-${renderNumber}-${frameNum}`}
        url={`https://www.latentculture.com/tohm/render_${renderNumber}/map/jpg${frameNum}/{z}/{x}-{y}.jpg`}
        noWrap={true}
        tileSize={config.tile_size}
        updateWhenZooming={true}
        updateWhenIdle={false}
        keepBuffer={2}
        minZoom={config.min_zoom_layer + 2 * !DetectDevice - 1}
        maxZoom={config.max_zoom_layer}
        // To increase background resolution on desktop, uncomment these lines
        minNativeZoom={1}
        //maxNativeZoom={1}
        maxNativeZoom={1}
        zIndex={3}
        opacity={1}
        bounds={[[0, 0], calculateTileBounds()]}
      />

      <TileLayer
        key={`second-${renderNumber}-${frameNum}`}
        url={`https://www.latentculture.com/tohm/render_${renderNumber}/map/jpg${frameNum}/{z}/{x}-{y}.jpg`}
        noWrap={true}
        tileSize={config.tile_size}
        updateWhenZooming={false}
        updateWhenIdle={true}
        keepBuffer={0}
        minZoom={config.min_zoom_layer + 2 * !DetectDevice - 1}
        maxZoom={config.max_zoom_layer}
        // To increase background resolution on desktop, uncomment these lines
        minNativeZoom={1}
        maxNativeZoom={7}
        opacity={1}
        bounds={[[0, 0], calculateTileBounds()]}
        zIndex={4}
      />

      {!isPaused ? (
        <MainTileLayer
          mainTL={mainTL}
          setAnimationStartTime={setAnimationStartTime}
          sync_flag={sync_flag}
          renderNumber={renderNumber}
          key={`webp-${renderNumber}`}
        />
      ) : null}

      <>
        {projectInfo ? (
          <Provider store={store}>
            <Information
              setProjectInfo={setProjectInfo}
              setSidebar={setSidebar}
              masterTile={masterTile}
              currentPrompt={currentPrompt}
              gridOverlay={gridOverlay}
              setMasterTile={setMasterTile}
              setSidebarTitle={setSidebarTitle}
              ownershipUpdated={ownershipUpdated}
              setCurrentPrompt={setCurrentPrompt}
              setTilePreview={setTilePreview}
              listViewExpanded={listViewExpanded}
              setlistViewExpanded={setlistViewExpanded}
              redactNFTs={redactNFTs}
              mintMessage={mintMessage}
              setMintMessage={setMintMessage}
              setRevertMessage={setRevertMessage}
              ownership={ownership}
              setOwnership={setOwnership}
              currFetchingItems={currFetchingItems}
              setCurrFetchingItems={setCurrFetchingItems}              
            />
          </Provider>
        ) : null}
      </>

      <>
        {sidebarState ? (
          <Provider store={store}>
            <Sidebar
              sidebarTitle={sidebarTitle}
              masterTile={masterTile}
              setPopout={setPopout}
              setSidebar={setSidebar}
              gridOverlay={gridOverlay}
              sidebarState={sidebarState}
              setMasterTile={setMasterTile}
              setSidebarTitle={setSidebarTitle}
              currentPrompt={currentPrompt}
              setCurrentPrompt={setCurrentPrompt}
              subregionMode={subregionMode}
              setSubregionMode={setSubregionMode}
              setMintMessage={setMintMessage}
              setRevertMessage={setRevertMessage}
              singleRegion={singleRegion}
              setSingleRegion={setSingleRegion}
              isPaused={isPaused}
              frameNum={frameNum}
              mintingPaused={mintingPaused}
              showBanner={showBanner}
              setShowBanner={setShowBanner}
              affiliate={affiliate}
              tilePreview={tilePreview}
              setTilePreview={setTilePreview}
              mintingOverride={mintingOverride}
              redactNFTs={redactNFTs}
              physicalClaimed={physicalClaimed}
              setphysicalClaimed={setphysicalClaimed}
              renderNumber={renderNumber}
              ownership={ownership}
              setOwnership={setOwnership}
            />
          </Provider>
        ) : null}
      </>

      <Provider store={store}>
        <Topbar
          handle={handle}
          overlayMode={overlayMode}
          setOverlayMode={setOverlayMode}
          setProjectInfo={setProjectInfo}
          sidebarState={sidebarState}
          setSidebar={setSidebar}
          setSelectedLayer={setSelectedLayer}
          sidebarTitle={sidebarTitle}
          mainTL={mainTL}
          gridOverlay={gridOverlay}
          masterTile={masterTile}
          setMasterTile={setMasterTile}
          setSidebarTitle={setSidebarTitle}
          projectInfo={projectInfo}
          setPopout={setPopout}
          setCurrentPrompt={setCurrentPrompt}
          setSingleRegion={setSingleRegion}
          setSubregionMode={setSubregionMode}
          promptOverlayRef={promptOverlayRef}
          setFrameNum={setFrameNum}
          frameNum={frameNum}
          isPaused={isPaused}
          setIsPaused={setIsPaused}
          animationStartTime={animationStartTime}
          setAnimationStartTime={setAnimationStartTime}
          sync_flag={sync_flag}
          showBanner={showBanner}
          setShowBanner={setShowBanner}
          bannerMessage={bannerMessage}
          listView={listView}
          setListView={setListView}
          ownershipUpdated={ownershipUpdated}
          setOwnershipUpdated={setOwnershipUpdated}
          setLeadingFrameNum={setLeadingFrameNum}
          setRedactNFTs={setRedactNFTs}
          redactNFTs={redactNFTs}
          setTilePreview={setTilePreview}
          renderNumber={renderNumber}
          setRenderNumber={setRenderNumber}
          showVersionToggle={showVersionToggle}
          setlistViewExpanded={setlistViewExpanded}
          ownership={ownership}
          setOwnership={setOwnership}
          currFetchingItems={currFetchingItems}
          setCurrFetchingItems={setCurrFetchingItems} 
        />
      </Provider>

      <>
        {popout ? (
          <Provider store={store}>
            <Popout
              sidebarTitle={sidebarTitle}
              masterTile={masterTile}
              setPopout={setPopout}
              setSidebar={setSidebar}
              sidebarState={sidebarState}
              setMasterTile={setMasterTile}
              gridOverlay={gridOverlay}
              setSidebarTitle={setSidebarTitle}
              currentPrompt={currentPrompt}
              mintMessage={mintMessage}
              setMintMessage={setMintMessage}
              setRevertMessage={setRevertMessage}
              singleRegion={singleRegion}
              setSingleRegion={setSingleRegion}
              setCurrentPrompt={setCurrentPrompt}
              setShowBanner={setShowBanner}
              mintingPaused={mintingPaused}
              showBanner={showBanner}
              affiliate={affiliate}
              setlistViewExpanded={setlistViewExpanded}
              setProjectInfo={setProjectInfo}
              redactNFTs={redactNFTs}
              physicalClaimed={physicalClaimed}
              setphysicalClaimed={setphysicalClaimed}
              renderNumber={renderNumber}
              ownership={ownership}
              setOwnership={setOwnership}
            />
          </Provider>
        ) : null}
      </>

      <ZoomControl position="bottomleft" />

      <StartAtRandomTile
        mainTL={mainTL}
        sync_flag={sync_flag}
        skipSplash={skipSplash}
      />

      <div id="frame-parent">
        <div id="frame"></div>
      </div>
      {/*<div id="frame-with-rounded-corners"></div>*/}
      <FrameWithRoundedCorners id="frame-with-rounded-corners"></FrameWithRoundedCorners>

      <Splash
        setProjectInfo={setProjectInfo}
        skipSplash={skipSplash}
        setListView={setListView}
        redactNFTs={redactNFTs}
        setRedactNFTs={setRedactNFTs}
      />
      <Provider store={store}>
        <MintMessages
          mintMessage={mintMessage}
          setMintMessage={setMintMessage}
          masterTile={masterTile}
          revertMessage={revertMessage}
          setRevertMessage={setRevertMessage}
          physicalClaimed={physicalClaimed}
          setphysicalClaimed={setphysicalClaimed}
        />
      </Provider>

      {DetectDevice ? <div id="mobile-darken"></div> : null}

      <>
        {!popout ? (
          <BottomBar
            handle={handle}
            sidebarState={sidebarState}
            setSelectedLayer={setSelectedLayer}
            setSidebar={setSidebar}
            setMasterTile={setMasterTile}
            setCurrentPrompt={setCurrentPrompt}
            setSubregionMode={setSubregionMode}
            setFrameNum={setFrameNum}
            frameNum={frameNum}
            isPaused={isPaused}
            setIsPaused={setIsPaused}
            animationStartTime={animationStartTime}
            setAnimationStartTime={setAnimationStartTime}
            sync_flag={sync_flag}
            mainTL={mainTL}
            leadingFrameRef={leadingFrameRef}
            setLeadingFrameNum={setLeadingFrameNum}
            tilePreview={tilePreview}
            setTilePreview={setTilePreview}
            setListView={setListView}
            renderNumber={renderNumber}
          />
        ) : null}
      </>
    </MapContainer>
  );
}

export default MyMap;
